import type { components } from "@/api-schema";
import { cn } from "@/common/helpers/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import i18next from "i18next";
import { MapPinIcon } from "lucide-react";
import { type ButtonHTMLAttributes, type ReactElement, type Ref } from "react";

type Branch = components["schemas"]["Branch"];
type Address = components["schemas"]["Address"];

type RelocationOrderLocationVariant = "branch" | "external" | "unknown";

export interface LocationDisplayProps {
  branch?: Branch | null;
  address?: Address | null;
  className?: string;
}

export interface RelocationOrderLocationDetails {
  displayLabel: string;
  tooltipLabel: string;
  variant: RelocationOrderLocationVariant;
}

const normalizeLocationValue = (value?: string | null): string | undefined => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : undefined;
};

const buildStreetLine = (street?: string | null, houseNumber?: string | null): string | undefined => {
  const streetLine = [normalizeLocationValue(street), normalizeLocationValue(houseNumber)]
    .filter(Boolean)
    .join(" ")
    .trim();
  return streetLine || undefined;
};

const buildCityLine = (postalCode?: string | null, city?: string | null): string | undefined => {
  const cityLine = [normalizeLocationValue(postalCode), normalizeLocationValue(city)].filter(Boolean).join(" ").trim();
  return cityLine || undefined;
};

export const buildRelocationAddressString = (
  company?: string | null,
  name?: string | null,
  street?: string | null,
  houseNumber?: string | null,
  postalCode?: string | null,
  city?: string | null,
): string => {
  const streetLine = buildStreetLine(street, houseNumber);
  const cityLine = buildCityLine(postalCode, city);

  return [normalizeLocationValue(company), normalizeLocationValue(name), streetLine, cityLine]
    .filter(Boolean)
    .join(", ");
};

const getExternalAddressDisplayLabel = (address: Address, unknownLabel: string): string => {
  const city = normalizeLocationValue(address.city);
  const cityLine = buildCityLine(address.postalCode, address.city);
  const streetLine = buildStreetLine(address.street, address.houseNumber);

  return (
    city ??
    cityLine ??
    normalizeLocationValue(address.company) ??
    normalizeLocationValue(address.recipientName) ??
    streetLine ??
    unknownLabel
  );
};

export const getRelocationOrderLocationDetails = ({
  branch,
  address,
  unknownLabel,
}: {
  branch?: Branch | null;
  address?: Address | null;
  unknownLabel: string;
}): RelocationOrderLocationDetails => {
  if (branch) {
    const branchLabel = branch.name;
    const fullAddress = buildRelocationAddressString(
      branch.company?.name ?? branch.address?.company,
      branch.name,
      branch.address?.street,
      branch.address?.houseNumber,
      branch.address?.postalCode,
      branch.address?.city,
    );

    return {
      displayLabel: branchLabel,
      tooltipLabel: fullAddress || branchLabel,
      variant: "branch",
    };
  }

  if (address) {
    const displayLabel = getExternalAddressDisplayLabel(address, unknownLabel);
    const fullAddress = buildRelocationAddressString(
      address.company,
      address.recipientName,
      address.street,
      address.houseNumber,
      address.postalCode,
      address.city,
    );

    return {
      displayLabel,
      tooltipLabel: fullAddress || displayLabel,
      variant: "external",
    };
  }

  return {
    displayLabel: unknownLabel,
    tooltipLabel: unknownLabel,
    variant: "unknown",
  };
};

type LocationTooltipButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { ref?: Ref<HTMLButtonElement> };

const LocationTooltipButton = ({ children, className, type, ref, ...props }: LocationTooltipButtonProps) => {
  return (
    <button
      ref={ref}
      className={cn(
        "flex min-w-0 cursor-help items-center rounded-sm border-0 bg-transparent p-0 text-left outline-none",
        className,
      )}
      type={type ?? "button"}
      {...props}
    >
      {children}
    </button>
  );
};
LocationTooltipButton.displayName = "LocationTooltipButton";

export function LocationDisplay({ branch, address, className }: LocationDisplayProps): ReactElement {
  const unknownLabel = i18next.t("common:Unknown");
  const details = getRelocationOrderLocationDetails({ branch, address, unknownLabel });
  const isExternalAddress = details.variant === "external";

  if (details.variant === "unknown") {
    return <span className={className ?? "truncate"}>{details.displayLabel}</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <LocationTooltipButton
            aria-label={details.tooltipLabel}
            className={cn(className, isExternalAddress && "text-primary")}
          >
            <span className="truncate">{details.displayLabel}</span>
            {isExternalAddress ? (
              <MapPinIcon aria-hidden="true" className="ml-1.5 h-4 w-4 shrink-0 opacity-70" />
            ) : null}
          </LocationTooltipButton>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="flex flex-col text-sm">
            <span className="font-medium">{details.tooltipLabel}</span>
            <span className="text-xs font-medium opacity-90">
              {i18next.t(isExternalAddress ? "fit:ExternalAddress" : "fit:Branch")}
            </span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
